import { useState } from "react";
import {
  Table,
  Popconfirm,
  Image,
  Spin,
  Empty,
  Switch,
  Tooltip,
  Pagination,
  Select,
  Card,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline } from "react-icons/io5";
import { BiEdit } from "react-icons/bi";
import { s3FallbackUrl } from "../../../utils/mediaFallback";

const { Option } = Select;

function DataTable(props: any) {
  const [loadingRows, setLoadingRows] = useState<{ [key: number]: boolean }>(
    {},
  );

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 50,
      render: (_text, _record, index) => {
        return <div>{(props.page - 1) * props.pagesize + index + 1}</div>;
      },
    },
    {
      title: "Image",
      key: "image",
      width: 80,
      align: "center",
      render: (_text, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {record?.attributes?.image ? (
              <Image
                key={record.id}
                src={record.attributes.image}
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 6,
                  objectFit: "cover",
                }}
                alt="Podcast"
                fallback={s3FallbackUrl(record.attributes.image) || undefined}
              />
            ) : (
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 6,
                  background: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  color: "#999",
                }}
              >
                No image
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Title",
      key: "title",
      width: 200,
      render: (_text, record: any) => {
        return (
          <div
            style={{ cursor: "pointer", fontSize: 13, fontWeight: 500 }}
            onClick={() => props.onEdit(record)}
          >
            {record?.attributes?.title || "Untitled"}
          </div>
        );
      },
    },
    {
      title: "Body",
      key: "body",
      width: 250,
      render: (_text, record: any) => {
        const body = record?.attributes?.body;
        return (
          <div style={{ fontSize: 12, color: "#666" }}>
            {body ? (
              body.length > 60 ? (
                body.substring(0, 60) + "..."
              ) : (
                body
              )
            ) : (
              <span style={{ color: "#bbb" }}>--</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Audio",
      key: "audio",
      width: 100,
      render: (_text, record: any) => {
        const audio = record?.attributes?.audio;
        return (
          <div style={{ fontSize: 12 }}>
            {audio ? (
              <a
                href={audio}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1890ff" }}
              >
                {audio.length > 20 ? audio.substring(0, 20) + "..." : audio}
              </a>
            ) : (
              <span style={{ color: "#bbb" }}>--</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "publishedAt",
      width: 140,
      align: "center",
      render: (_text, record: any) => {
        const isPublished = !!record?.attributes?.publishedAt;
        const isLoading = loadingRows[record.id] || false;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={isPublished ? "Click to unpublish" : "Click to publish"}
            >
              <Switch
                checked={isPublished}
                loading={isLoading}
                disabled={isLoading}
                size="default"
                checkedChildren={
                  <span style={{ fontSize: "10px" }}>Published</span>
                }
                unCheckedChildren={
                  <span style={{ fontSize: "10px" }}>Draft</span>
                }
                style={{ minWidth: "60px" }}
                onChange={(checked) => {
                  if (props.onTogglePublish) {
                    setLoadingRows((prev) => ({ ...prev, [record.id]: true }));
                    props.onTogglePublish(record, checked, () => {
                      setLoadingRows((prev) => {
                        const newState = { ...prev };
                        delete newState[record.id];
                        return newState;
                      });
                    });
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <BiEdit
              size={20}
              color="#f5a442"
              style={{ cursor: "pointer" }}
              onClick={() => props.onEdit(record)}
            />
            <Popconfirm
              title="Delete Podcast"
              description="Are you sure you want to delete this podcast?"
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              onConfirm={() => props.onDelete(record.id)}
            >
              <IoTrashOutline
                color="red"
                size={20}
                style={{ cursor: "pointer" }}
              />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        marginTop: 10,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={props.data}
        pagination={false}
        size="small"
        rowKey={(record: any) => record.id}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No podcasts found"
            />
          ),
        }}
      />
      <div
        style={{
          borderTop: "1px solid #f0f0f0",
          padding: "12px 16px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {props.loading ? (
          <Spin size="small" />
        ) : (
          <>
            <Pagination
              responsive
              current={props?.page}
              total={props.meta}
              pageSize={props?.pagesize}
              showSizeChanger={false}
              onChange={(p, t) => {
                props.onPageChange(p, t);
              }}
            />
            <Select
              placeholder="Page size"
              style={{ width: 100 }}
              value={props?.pagesize}
              onChange={(value) => {
                props.onPageChange(1, value);
              }}
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </>
        )}
      </div>
    </Card>
  );
}

export default DataTable;
